import { FC, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import Lottie from 'lottie-react';
import { LoadingSpinnerAnimation } from 'assets/animations';
import { ArrowRightIcon, NavCloseIcon } from 'assets/icons';
import { GenericError, NoPermissions, SidePanel } from 'components';
import { VoidFn } from 'types/baseTypes';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  selectCurrentTeam,
  selectCurrentTeamInvoicePermissions,
  selectCurrentTeamRefundPermissions,
  selectUserDetails
} from 'containers/app/appSlice';
import { NotifierTypes } from 'constants/notifierConstants';
import { updateRefundAmount, updateRefundReason } from 'containers/receipts/receiptsSlice';
import { showNotification } from 'utils/Notifier';
import { useConfirmRefundMutation } from 'containers/receipts/api';
import { useLazyGetInvoiceRefundableTransactionsQuery } from '../api';
import { TransactionV2 } from '../invoices.model';
import TransactionCardV2 from './TransactionCardV2';
import { SidePanelRouteState } from 'routes/types';
import { DEFAULT_SIDE_PANEL_ROUTE_STATE, FROM_SIDE_PANEL_ROUTE_STATE } from 'routes/constants';
import { InfoMesssages } from 'types/infoMessages';

interface IssueRefundPanelProps {
  onClose?: VoidFn;
}

const IssueRefundPanel: FC<IssueRefundPanelProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const invoicePermissions = useAppSelector(selectCurrentTeamInvoicePermissions);
  const currentRefundPermissions = useAppSelector(selectCurrentTeamRefundPermissions);

  const {
    rootInvoiceId,
    invoiceId: invoiceIdFromUrl,
    rootReceiptId,
    rootPage,
    rootCustomerId,
    rootDisputeId,
    rootJobId,
    rootEstimateId
  } = useParams();

  const { handleSidePanelClose } = useOutletContext<{ handleSidePanelClose: () => void }>();
  const currentUser = useAppSelector(selectUserDetails);
  const [showBackArrow, setShowBackArrow] = useState(false);
  const [hasPermissionsToView, setHasPermissionsToView] = useState(true);

  const [getInvoiceRefundableTransactions, { data: transactionsData, isLoading, isSuccess, isError }] =
    useLazyGetInvoiceRefundableTransactionsQuery();

  const [confirmRefundQuery] = useConfirmRefundMutation();

  const invoiceId = invoiceIdFromUrl || rootInvoiceId;

  useEffect(() => {
    if (currentTeam?.id && invoiceId) {
      getInvoiceRefundableTransactions({
        teamId: currentTeam.id.toString(),
        invoiceId: invoiceId
      });
    }
  }, [currentTeam?.id, invoiceId, getInvoiceRefundableTransactions]);

  useEffect(() => {
    const routeState = location.state as SidePanelRouteState;
    if (routeState?.fromSidePanel) {
      setShowBackArrow(true);
    }
    let routeStateTimeout;
    if (routeState?.shouldAnimate) {
      routeStateTimeout = setTimeout(() => {
        navigate(location.pathname, { replace: true, state: DEFAULT_SIDE_PANEL_ROUTE_STATE });
      }, 800);
    }
    return () => {
      clearTimeout(routeStateTimeout);
    };
  }, [location, navigate]);

  useEffect(() => {
    if (!invoicePermissions.viewInvoicesByTeam && !invoicePermissions.viewInvoicesBySelf) {
      setHasPermissionsToView(false);
    }
  }, [invoicePermissions]);

  const shouldEnableRefundFromInvoice = (refundPermissions, isCreatedBySelf) => {
    return refundPermissions.createRefundsByTeam || (isCreatedBySelf && refundPermissions.createRefundsBySelf);
  };

  const getRefundDisabledReasonFromInvoice = (refundPermissions, isCreatedBySelf) => {
    if (!(refundPermissions.createRefundsByTeam || (isCreatedBySelf && refundPermissions.createRefundsBySelf))) {
      return InfoMesssages.refundNoPermissionMessage;
    }
    return 'Refund is not allowed for this payment method';
  };

  const onSidePanelClose = useCallback(() => {
    if (showBackArrow) {
      navigate(-1);
    } else {
      handleSidePanelClose();
    }
  }, [showBackArrow, navigate, handleSidePanelClose]);

  const handleNavigateToCreateRefund = (transactionId: string) => {
    if (rootInvoiceId) {
      navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/create-refund/${transactionId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/create-refund/${transactionId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam?.id}/${rootReceiptId}/create-refund/${transactionId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootCustomerId) {
      navigate(`/customers/${currentTeam?.id}/${rootCustomerId}/create-refund/${transactionId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootDisputeId) {
      navigate(`/payments/disputes/${currentTeam?.id}/${rootDisputeId}/create-refund/${transactionId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootJobId) {
      navigate(`/jobs/${currentTeam?.id}/${rootJobId}/create-refund/${transactionId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootEstimateId) {
      navigate(`/estimates/${currentTeam?.id}/${rootEstimateId}/create-refund/${transactionId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    }
  };

  const handleRefund = async (transactionId: string) => {
    try {
      const data = await confirmRefundQuery({
        teamId: currentTeam.id.toString(),
        transactionId: transactionId,
        skipAmountCheck: true
      }).unwrap();
      if (data) handleNavigateToCreateRefund(transactionId);
    } catch (error: unknown) {
      const errorMessage = (error as { data?: { details?: string } })?.data?.details ?? 'Something went wrong';
      showNotification(dispatch, NotifierTypes.ERROR, errorMessage, 5000);
    }
  };

  const isCreatedBySelf = (transactionCreatedBy: string) => {
    return transactionCreatedBy === currentUser?.id;
  };

  const handleTransactionClick = (transactionId: string) => {
    dispatch(updateRefundReason(''));
    dispatch(updateRefundAmount(''));
    handleRefund(transactionId);
  };

  return (
    <SidePanel isOpen={true} onClose={onSidePanelClose} shouldAnimate={location?.state?.shouldAnimate}>
      <div className="flex h-full w-96 max-w-sm flex-col">
        {isLoading ? (
          <div className="flex h-full w-96 items-center justify-center">
            <Lottie className="h-28 w-28" animationData={LoadingSpinnerAnimation} loop={true} />
          </div>
        ) : (
          isSuccess && (
            <>
              <div className="flex items-center gap-2 bg-white py-5 px-4">
                <div onClick={onSidePanelClose} className="cursor-pointer">
                  {showBackArrow ? (
                    <ArrowRightIcon className="rotate-180" id="issue-refund-panel-back-arrow" />
                  ) : (
                    <NavCloseIcon id="issue-refund-panel-close" />
                  )}
                </div>
                <div id="issue-refund-panel-head" className="text-17px font-semibold text-headingGray">
                  Issue refund
                </div>
              </div>

              {hasPermissionsToView ? (
                <div
                  className="customNormalScroll flex max-w-sm grow flex-col gap-3 overflow-y-auto 
                overflow-x-hidden px-4 py-3">
                  <>
                    <div className="mb-3 text-sbase font-semibold text-primaryText">
                      Choose the payment against which you want to issue a refund.
                    </div>

                    {transactionsData && transactionsData?.length > 0 ? (
                      <div className="h-full space-y-3 divide-y divide-[#D8DFE580]">
                        {transactionsData?.map((transaction: TransactionV2) => {
                          const isRefundDisabled = !shouldEnableRefundFromInvoice(
                            currentRefundPermissions,
                            isCreatedBySelf(transaction.transactionCreatedBy)
                          );

                          const disabledReason = getRefundDisabledReasonFromInvoice(
                            currentRefundPermissions,
                            isCreatedBySelf(transaction.transactionCreatedBy)
                          );

                          const handleTxnClick = (id: string) => {
                            if (isRefundDisabled) {
                              showNotification(dispatch, NotifierTypes.WARNING, disabledReason, 5000);
                            } else {
                              handleTransactionClick(id);
                            }
                          };

                          return (
                            <div
                              key={transaction.transactionId}
                              className={`divide-y divide-secondaryBtn ${isRefundDisabled ? 'opacity-50' : ''}`}>
                              <TransactionCardV2
                                transaction={{ ...transaction, paymentMethodName: transaction.paymentMethod }}
                                onTxnClick={handleTxnClick}
                                shouldCheckPermission={false}
                              />
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="py-8 text-center text-gray-500">
                        <div className="text-sm">No transactions found for this invoice</div>
                      </div>
                    )}
                  </>
                </div>
              ) : (
                <NoPermissions />
              )}
            </>
          )
        )}
        {!isLoading && !isSuccess && isError && <GenericError handleErrorBack={onSidePanelClose} />}
      </div>
    </SidePanel>
  );
};

export default IssueRefundPanel;
